import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from './context/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { Dashboard } from './pages/Dashboard'
import { DashboardLayout } from './components/layout/DashboardLayout'
import Invoices from './pages/Invoices'
import InvoiceCreate from './pages/InvoiceCreate'
import InvoiceDetail from './pages/InvoiceDetail'
import IncomingInvoices from './pages/IncomingInvoices'
import IncomingInvoiceCreate from './pages/IncomingInvoiceCreate'
import IncomingInvoiceDetail from './pages/IncomingInvoiceDetail'
import QuarterlyReports from './pages/QuarterlyReports'
import QuarterlyReportDetail from './pages/QuarterlyReportDetail'
import AuditLog from './pages/AuditLog'
import Settings from './pages/Settings'
import Onboarding from './pages/Onboarding'
import AdminUsers from './pages/AdminUsers'
import AdminUserDetail from './pages/AdminUserDetail'
import { Toaster } from './components/ui/toaster'
import { queryClient } from './lib/queryClient'
import './i18n'

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Onboarding route (protected, outside dashboard layout) */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              {/* Outgoing invoice routes */}
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/create" element={<InvoiceCreate />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              {/* Incoming invoice routes */}
              <Route path="incoming-invoices" element={<IncomingInvoices />} />
              <Route path="incoming-invoices/create" element={<IncomingInvoiceCreate />} />
              <Route path="incoming-invoices/:id" element={<IncomingInvoiceDetail />} />
              {/* Quarterly reports */}
              <Route path="quarterly-reports" element={<QuarterlyReports />} />
              <Route path="quarterly-reports/:id" element={<QuarterlyReportDetail />} />
              {/* Placeholder routes - to be implemented */}
              <Route path="users" element={<PlaceholderPage title="Usuarios" />} />
              {/* <Route path="sales" element={<PlaceholderPage title="Ventas" />} /> */}
              {/* <Route path="products" element={<PlaceholderPage title="Productos" />} /> */}
              {/* <Route path="reports" element={<PlaceholderPage title="Reportes" />} /> */}
              <Route path="settings" element={<Settings />} />
              {/* Admin routes */}
              <Route path="admin/users" element={<AdminUsers />} />
              <Route path="admin/users/:id" element={<AdminUserDetail />} />
              <Route path="admin/audit-log" element={<AuditLog />} />
            </Route>

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Placeholder component for unimplemented pages
function PlaceholderPage({ title }: { title: string }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      <p className="text-muted-foreground">
        {t('placeholder.underConstruction')}
      </p>
    </div>
  )
}

export default App
